import { TC } from '../constants.js';
import { $, dv, mk, mkUpload } from '../dom.js';
import { refreshDesignPreview } from '../build/design.js';
import { syncFlatTokens } from '../prompts/context.js';
import { ST } from '../state.js';

// ── BRAND PANEL ──────────────────────────────────────────────────
// Renders into #design-brand-panel inside the design sidebar.

// Module-scoped regen handler — registered by build/concept.js to avoid circular imports
let _regenFn = null;
const setRegenHandler = (fn) => { _regenFn = fn; };

// ── PURE DATA FUNCTIONS ──────────────────────────────────────────
// No DOM dependency — become Vue computed properties during migration.

/** Image type display order for the image manager. */
const IMG_TYPES = ['logo', 'hero', 'product', 'team', 'lifestyle', 'icon', 'screenshot', 'other'];

/** Maps image type to its default placement description. */
const IMG_PLACEMENTS = {
  logo: 'nav and footer',
  hero: 'hero background',
  product: 'features or services section',
  team: 'about section',
  lifestyle: 'page imagery',
  icon: 'feature icons',
  screenshot: 'reference only',
  other: 'page imagery',
};

/**
 * Resolve the best available logo preview source.
 * Priority: uploaded file → URL → marketing brief → scraped site.
 * @returns {{ src: string, label: string } | null}
 */
const resolveLogoPreview = () => {
  if (ST.logoImg?.data && ST.logoImg.analysis?.useDecision !== 'skip') {
    return { src: ST.logoImg.preview || '', label: ST.logoImg.name || 'logo' };
  }
  if (ST.logoUrl?.trim()) {
    return { src: ST.logoUrl.trim(), label: 'Logo URL' };
  }
  if (ST.marketingBrief?.logoUrl?.trim()) {
    return { src: ST.marketingBrief.logoUrl, label: 'From website' };
  }
  const scraped = ST.existingScrape?.logos?.find(l => l.src);
  if (scraped) {
    return { src: scraped.src, label: 'From existing site' };
  }
  return null;
};

/**
 * Collect all images from uploads, logo, brief analysis, and stock search.
 * @returns {{ uploaded: Array, brief: Array, stock: Array }}
 */
const collectBrandImages = () => {
  const uploaded = [];
  if (ST.uploads?.images?.length) {
    ST.uploads.images.forEach(img => {
      if (img.analysis) uploaded.push(img);
    });
  }
  if (ST.logoImg?.data) {
    if (!ST.logoImg.analysis) {
      ST.logoImg.analysis = /** @type {import('../types.ts').ImageAnalysis} */ ({ type: 'logo', placement: 'nav and footer', useDecision: 'use', filename: ST.logoImg.name || 'logo', reason: 'User-provided logo' });
    }
    uploaded.push({
      name: ST.logoImg.name || 'Logo',
      preview: ST.logoImg.preview || `data:${ST.logoImg.type || 'image/png'};base64,${ST.logoImg.data}`,
      data: ST.logoImg.data,
      type: ST.logoImg.type,
      analysis: ST.logoImg.analysis,
      isLogo: true,
    });
  }
  const brief = (ST.marketingBrief?.images || []).filter(i => i.src && !i.src.startsWith('data:'));
  const stock = ST.stockImages || [];
  return { uploaded, brief, stock };
};

/**
 * Group images by their analysis type, ordered by IMG_TYPES.
 * @param {Array} images
 * @returns {Record<string, Array>}
 */
const groupImagesByType = (images) => {
  /** @type {Record<string, any[]>} */
  const groups = {};
  images.forEach(img => {
    const t = img.analysis?.type || 'other';
    if (!groups[t]) groups[t] = [];
    groups[t].push(img);
  });
  return groups;
};

/** @returns {boolean} Whether the concept has contact/subscribe sections. */
const hasFormSection = () =>
  (ST.concept?.sections || []).some(s => s.type === 'contact' || s.type === 'subscribe');

// ── STATE ACTIONS ────────────────────────────────────────────────
// Named mutations — become Pinia actions during migration.

const editColorToken = (key) => {
  const c = ST.concept;
  const tok = c?.designTokens?.colors?.[key];
  if (!tok) return;
  const newHex = prompt(`Edit ${tok.name || key} (${tok.value}):`, tok.value);
  if (newHex && /^#[0-9a-fA-F]{3,8}$/.test(newHex.trim())) {
    tok.value = newHex.trim();
    syncFlatTokens(c);
    refreshDesignPreview();
    renderBrandPanel();
  }
};

const setImageUse = (img, decision) => {
  img.analysis.useDecision = decision;
  ST.uploadImgMap = null;
  renderBrandPanel();
};

const setImageType = (img, newType) => {
  img.analysis.type = newType;
  img.analysis.placement = IMG_PLACEMENTS[newType] || 'page imagery';
  ST.uploadImgMap = null;
  renderBrandPanel();
};

const setStockExcluded = (img, excluded) => {
  img.excluded = excluded;
  renderBrandPanel();
};

const setNotificationEmail = (value) => {
  ST.notificationEmail = value.trim();
  localStorage.setItem('sf_notify_email', ST.notificationEmail);
};

const setCustomFont = (name) => {
  ST.purchasedFont = name;
  renderBrandPanel();
};

// ── DOM RENDERING ────────────────────────────────────────────────
// Imperative DOM — replaced by Vue templates during migration.

function renderBrandPanel() {
  const el = $('design-brand-panel');
  if (!el) return;
  const c = ST.concept;
  if (!c) {
    el.innerHTML = '<div style="padding:20px;text-align:center;color:var(--txt2)">No concept generated yet.</div>';
    return;
  }

  el.innerHTML = '';
  const dt = c.designTokens;
  const hf = `'${c.fonts?.heading || 'DM Sans'}',sans-serif`;
  const bf = `'${c.fonts?.body || 'DM Sans'}',sans-serif`;

  // ── Colors ──
  const colorsSection = mk('div', { class: 'bp-section' });
  colorsSection.append(mk('div', { class: 'bp-label' }, 'Colors'));
  const swatchRow = mk('div', { class: 'bp-swatch-row' });
  if (dt?.colors) {
    Object.entries(dt.colors).forEach(([key, tok]) => {
      const wrap = mk('div', { class: 'br-swatch-wrap', onclick: () => editColorToken(key) });
      wrap.append(
        mk('div', { class: 'br-swatch', style: `background:${tok.value}` }),
        mk('div', { class: 'br-swatch-role' }, tok.name || key),
      );
      swatchRow.append(wrap);
    });
  }
  colorsSection.append(swatchRow);
  el.append(colorsSection);

  // ── Fonts ──
  const fontsSection = mk('div', { class: 'bp-section' });
  fontsSection.append(mk('div', { class: 'bp-label' }, 'Typography'));
  const headingStyle = dt?.typography?.heading?.style || '';
  const bodyStyle = dt?.typography?.body?.style || '';
  fontsSection.append(
    mk('div', { class: 'fh', style: `font-family:${hf};color:var(--txt)` }, 'The quick brown fox'),
    mk('span', { class: 'ftag' }, (c.fonts?.heading || '—') + (headingStyle ? ` — ${headingStyle}` : '')),
    mk('div', { class: 'fb', style: `font-family:${bf};color:var(--txt2)` }, 'Pack my box with five dozen.'),
    mk('span', { class: 'ftagb' }, (c.fonts?.body || '—') + (bodyStyle ? ` — ${bodyStyle}` : '')),
  );
  el.append(fontsSection);

  // ── Sections ──
  const sectsSection = mk('div', { class: 'bp-section' });
  sectsSection.append(mk('div', { class: 'bp-label' }, `${(c.sections || []).length} sections planned`));
  const sectList = mk('div', { class: 'bp-sect-list' });
  (c.sections || []).forEach((s, i) => {
    const tclr = TC[s.type] || '#64748b';
    const row = mk('div', { class: 'bp-sect-row' });
    row.append(
      mk('span', { style: 'color:var(--txt3);font-size:11px;width:16px;flex-shrink:0' }, `${i + 1}.`),
      mk('span', { class: 'sect-pill', style: `background:${tclr}22;color:${tclr}` }, s.type),
      mk('span', { class: 'bp-sect-title' }, s.title),
    );
    sectList.append(row);
  });
  sectsSection.append(sectList);
  el.append(sectsSection);

  // ── Images ──
  renderBrandImages(el);

  // ── Notification email ──
  if (hasFormSection()) {
    const notifySection = mk('div', { class: 'bp-section' });
    notifySection.append(mk('div', { class: 'bp-label' }, 'Notification email'));
    notifySection.append(
      mk('p', { style: 'font-size:11px;color:var(--txt2);margin-bottom:6px' }, 'Form submissions will be sent here.'),
    );
    const inp = mk('input', {
      class: 'inp',
      type: 'email',
      placeholder: 'your@email.com',
      style: 'width:100%;font-size:12px',
      oninput(e) { setNotificationEmail(/** @type {HTMLInputElement} */ (e.target).value); },
    });
    /** @type {HTMLInputElement} */ (inp).value = ST.notificationEmail || '';
    notifySection.append(inp);
    el.append(notifySection);
  }

  // ── Brand assets (collapsed) ──
  const assetsSection = mk('div', { class: 'bp-section' });
  const assetsTitle = mk('div', {
    class: 'bp-label',
    style: 'cursor:pointer;user-select:none',
    onclick() {
      const g = /** @type {HTMLElement | null} */ (assetsSection.querySelector('.ba-grid'));
      if (g) g.style.display = g.style.display === 'none' ? '' : 'none';
    },
  });
  assetsTitle.textContent = '▸ Brand assets';
  assetsSection.append(assetsTitle);
  const grid = mk('div', { class: 'ba-grid', style: 'display:none' });
  // Custom font
  const fontRow = mk('div', { class: 'ba-row' });
  fontRow.append(mk('span', { class: 'ba-row-label' }, 'Custom font'));
  const fontBody = mk('div', { class: 'ba-row-body' });
  if (ST.purchasedFont) {
    const fset = mk('div', { class: 'ba-font-set' });
    fset.append(
      mk('span', { style: 'font-size:14px' }, 'Aa'),
      mk('span', { class: 'ba-font-set-name' }, ST.purchasedFont),
      mk('button', { class: 'ba-font-clear', onclick: () => setCustomFont('') }, 'x'),
    );
    fontBody.append(fset);
  } else {
    const fi = mk('input', { class: 'ba-font-inp', type: 'text', placeholder: 'e.g. Satoshi, Clash Display' });
    const commitFont = () => { const v = /** @type {HTMLInputElement} */ (fi).value.trim(); if (v) setCustomFont(v); };
    fi.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); commitFont(); } });
    fi.addEventListener('blur', commitFont);
    fontBody.append(fi);
  }
  fontRow.append(fontBody);
  grid.append(fontRow);
  // Brand guide
  const guideRow = mk('div', { class: 'ba-row' });
  guideRow.append(mk('span', { class: 'ba-row-label' }, 'Brand guide'));
  const guideBody = mk('div', { class: 'ba-row-body' });
  guideBody.append(mkUpload('Upload brand guide', 'PDF screenshot, style guide', ST.brandGuideImg,
    img => { ST.brandGuideImg = img; renderBrandPanel(); },
    () => { ST.brandGuideImg = null; renderBrandPanel(); },
  ));
  guideRow.append(guideBody);
  grid.append(guideRow);
  // Logo (only if none)
  if (!ST.logoImg) {
    const logoRow = mk('div', { class: 'ba-row' });
    logoRow.append(mk('span', { class: 'ba-row-label' }, 'Logo'));
    const logoBody = mk('div', { class: 'ba-row-body' });
    logoBody.append(mkUpload('Upload your logo', 'PNG, JPG', ST.logoImg,
      img => { ST.logoImg = img; renderBrandPanel(); },
      () => { ST.logoImg = null; renderBrandPanel(); },
    ));
    logoRow.append(logoBody);
    grid.append(logoRow);
  }
  assetsSection.append(grid);
  el.append(assetsSection);

  // ── Logo reference ──
  const logo = resolveLogoPreview();
  if (logo) {
    const logoSection = mk('div', { class: 'bp-section' });
    logoSection.append(mk('div', { class: 'bp-label' }, 'Logo reference'));
    const lp = dv('display:flex;align-items:center;gap:8px;padding:5px 8px;background:var(--s2);border-radius:6px');
    const li = mk('img', { style: 'width:26px;height:26px;object-fit:contain;border-radius:4px;flex-shrink:0', src: logo.src });
    li.onerror = () => { li.style.display = 'none'; };
    lp.append(li, mk('span', { style: 'font-size:10px;color:var(--txt2);overflow:hidden;text-overflow:ellipsis;white-space:nowrap' }, logo.label));
    logoSection.append(lp);
    el.append(logoSection);
  }

  // ── Regenerate button ──
  const regenSection = mk('div', { style: 'padding-top:8px' });
  regenSection.append(mk('button', {
    class: 'btn btn-g btn-sm',
    style: 'width:100%',
    onclick() { _regenFn?.(); },
  }, '↻ Regenerate concept'));
  el.append(regenSection);
}

// ── IMAGE MANAGER (DOM) ──────────────────────────────────────────

/**
 * Render the image manager into a container element.
 * @param {HTMLElement} container
 */
function renderBrandImages(container) {
  const { uploaded, brief, stock } = collectBrandImages();
  if (!(uploaded.length || brief.length || stock.length)) return;

  const section = mk('div', { class: 'bp-section' });
  section.append(mk('div', { class: 'bp-label' }, 'Images for build'));

  // ── Uploaded images grouped by type ──
  if (uploaded.length) {
    section.append(mk('div', { class: 'cimg-group-label', style: 'margin-bottom:6px;color:var(--acc)' }, 'Uploaded'));
    const groups = groupImagesByType(uploaded);

    IMG_TYPES.forEach(type => {
      const items = groups[type];
      if (!items) return;
      const group = mk('div', { class: 'cimg-group' });
      group.append(mk('div', { class: 'cimg-group-label' }, `${type} (${items.length})`));
      const imgGrid = mk('div', { class: 'cimg-grid' });

      items.forEach(img => {
        const excluded = img.analysis?.useDecision !== 'use';
        const card = mk('div', { class: 'cimg-card' + (excluded ? ' excluded' : '') });
        const thumbSrc = img.preview || `data:${img.type || 'image/jpeg'};base64,${img.data}`;
        card.append(mk('img', { class: 'cimg-thumb', src: thumbSrc, alt: img.name, title: img.name }));

        if (excluded) {
          card.append(mk('div', { style: 'padding:3px' },
            mk('button', { class: 'cimg-restore', onclick: () => setImageUse(img, 'use') }, '+ Restore'),
          ));
        } else {
          const actions = mk('div', { class: 'cimg-actions' });
          const sel = mk('select', { class: 'cimg-sel', title: 'Image placement',
            onchange: (e) => setImageType(img, /** @type {HTMLSelectElement} */ (e.target).value),
          });
          IMG_TYPES.forEach(t => {
            const opt = /** @type {HTMLOptionElement} */ (mk('option', { value: t }, t));
            if (t === (img.analysis?.type || 'other')) opt.selected = true;
            sel.append(opt);
          });
          actions.append(sel);
          actions.append(mk('button', { class: 'cimg-rm', title: 'Exclude from build',
            onclick: () => setImageUse(img, 'skip'),
          }, '×'));
          card.append(actions);
        }
        imgGrid.append(card);
      });
      group.append(imgGrid);
      section.append(group);
    });
  }

  // ── Brief images (from Business DNA analysis) ──
  if (brief.length) {
    section.append(mk('div', { class: 'cimg-group-label', style: 'margin-top:12px;margin-bottom:6px;color:var(--teal,#0a9e87)' }, `From website (${brief.length})`));
    const imgGrid = mk('div', { class: 'cimg-grid' });
    brief.forEach(img => {
      const card = mk('div', { class: 'cimg-card' });
      const thumb = mk('img', { class: 'cimg-thumb', src: img.src, alt: img.alt || '', title: img.alt || img.src });
      thumb.onerror = function () { /** @type {HTMLElement} */ (this.parentNode).style.display = 'none'; };
      card.append(thumb);
      card.append(mk('div', { class: 'cimg-meta' }, mk('span', { class: 'cimg-label' }, img.category || 'image')));
      imgGrid.append(card);
    });
    section.append(imgGrid);
  }

  // ── Stock images ──
  if (stock.length) {
    if (uploaded.length || brief.length) {
      section.append(mk('div', { style: 'height:1px;background:var(--bd2);margin:10px 0' }));
    }
    section.append(mk('div', { class: 'cimg-group-label', style: 'margin-bottom:6px;color:var(--txt3)' }, `Stock photos (${stock.length})`));
    const imgGrid = mk('div', { class: 'cimg-grid' });
    stock.forEach(img => {
      const card = mk('div', { class: 'cimg-card' + (img.excluded ? ' excluded' : '') });
      const thumbUrl = img.srcMedium || img.src;
      card.append(mk('img', { class: 'cimg-thumb', src: thumbUrl, alt: img.alt || 'stock photo',
        title: (img.alt || 'stock photo') + (img.forSection === '_general' ? '' : ` [${img.forSection}]`),
      }));

      if (img.excluded) {
        card.append(mk('div', { style: 'padding:3px' },
          mk('button', { class: 'cimg-restore', onclick: () => setStockExcluded(img, false) }, '+ Restore'),
        ));
      } else {
        const actions = mk('div', { class: 'cimg-actions' });
        const sects = ST.concept?.sections || [];
        const sel = mk('select', { class: 'cimg-sel', title: 'Assign to section', onchange(e) { img.forSection = /** @type {HTMLSelectElement} */ (e.target).value; } });
        sel.append(mk('option', { value: '_general' }, 'general'));
        sects.forEach(s => {
          const opt = /** @type {HTMLOptionElement} */ (mk('option', { value: s.id }, s.type));
          if (s.id === img.forSection) opt.selected = true;
          sel.append(opt);
        });
        if (!img.forSection || img.forSection === '_general') /** @type {HTMLOptionElement} */ (sel.firstChild).selected = true;
        actions.append(sel);
        actions.append(mk('button', { class: 'cimg-rm', title: 'Exclude from build',
          onclick: () => setStockExcluded(img, true),
        }, '×'));
        card.append(actions);
      }
      imgGrid.append(card);
    });
    section.append(imgGrid);
  }

  container.append(section);
}

// ── MODULE EXPORTS ──────────────────────────────────────────────
export {
  IMG_TYPES,
  IMG_PLACEMENTS,
  collectBrandImages,
  groupImagesByType,
  resolveLogoPreview,
  renderBrandPanel,
  renderBrandImages,
  setRegenHandler,
};
